/**
 * Shared utility functions for Knowledge Base pages
 */

/**
 * Format file size in human-readable format
 * Used in: KBUploadPage, KBDocumentsPage, KBValidationPage
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Format date in DD MMM YYYY, HH:MM format
 * Used in: KBDocumentsPage
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format date as relative time (e.g., "5m ago", "2h ago", "3d ago")
 * Used in: KBValidationPage
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

/**
 * Format date in full ISO-like format (YYYY-MM-DD HH:MM:SS)
 * Used in: KBValidationPage (tooltips)
 */
export const formatFullDate = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Convert file size in MB to bytes
 * Used for filter params
 */
export const mbToBytes = (mb: string): number | undefined => {
  if (!mb) return undefined;
  return parseFloat(mb) * 1024 * 1024;
};
